import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getProductDetail,
  createProduct,
  getBestSellerProduct,
  getScreensaverData,
  getStoreDataAction,
  addFavourite,
  getFavourites,
  deleteFavourite
} from '../../actions/customizer';
import { RootState } from '../../store';
import { AnyObject } from '../../../utils/basic';
import { IFavourite } from '../../types/builder';
import AuthManager from '../../../utils/AuthManager';

export interface CustomizerState {
  isFetching: boolean;
  product?: any;
  createdProduct: null | AnyObject;
  bestSellerProduct: null | AnyObject;
  screensaverData: null | AnyObject;
  storeData: null | AnyObject;
  selectedShippingPriority?: string;
  favourites: IFavourite[];
}

const initialState: CustomizerState = {
  isFetching: false,
  product: undefined,
  createdProduct: null,
  bestSellerProduct: null,
  screensaverData: null,
  storeData: null,
  selectedShippingPriority: undefined,
  favourites: []
};

// The favorites endpoint answers with a { result, data } envelope but has also returned a bare array. A failed
// envelope or any other shape has to land as [] — every `favourites.length`/`.some()` read downstream assumes one.
const toFavouriteList = (payload: any): IFavourite[] => {
  if (payload?.result === false) return [];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const customizerSlice = createSlice({
  name: 'customizer',
  initialState: initialState,
  reducers: {
    setProductData: (state: CustomizerState, action: PayloadAction<object>) => {
      state.product = action.payload;
    },
    resetCreatedProduct: (state: CustomizerState) => {
      state.createdProduct = null;
    },
    setBestSellerProduct: (state: CustomizerState, action: PayloadAction<object>) => {
      state.bestSellerProduct = action.payload;
    },
    setSelectedShippingPriority: (state: CustomizerState, action: PayloadAction<string>) => {
      state.selectedShippingPriority = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductDetail.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getProductDetail.fulfilled, (state, action) => {
        state.isFetching = false;
        state.product = action.payload;
      })
      .addCase(getProductDetail.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(createProduct.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isFetching = false;
        state.createdProduct = action.payload;
      })
      .addCase(createProduct.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(getBestSellerProduct.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getBestSellerProduct.fulfilled, (state, action) => {
        state.isFetching = false;
        state.bestSellerProduct = action.payload;
      })
      .addCase(getBestSellerProduct.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(getScreensaverData.fulfilled, (state, action) => {
        state.screensaverData = action.payload;
      })
      .addCase(getScreensaverData.rejected, (state) => {
        state.screensaverData = null;
      })
      .addCase(getStoreDataAction.fulfilled, (state, action) => {
        state.storeData = action.payload;
      })
      .addCase(getStoreDataAction.rejected, (state) => {
        state.storeData = null;
      })
      .addCase(addFavourite.fulfilled, (_state, action: any) => {
        AuthManager.setAnonymousUserIfNotExists(action.payload.user_id);
      })
      .addCase(getFavourites.fulfilled, (state, action: any) => {
        state.favourites = toFavouriteList(action.payload);
      })
      .addCase(getFavourites.rejected, (state) => {
        state.favourites = [];
      })
      .addCase(deleteFavourite.fulfilled, (state, action) => {
        const deletedUrl = action.meta.arg.product_url;
        state.favourites = (state.favourites || []).filter((f) => f.product_url !== deletedUrl);
      });
  }
});

export const { setProductData, resetCreatedProduct, setSelectedShippingPriority } = customizerSlice.actions;

const customizerReducer = customizerSlice.reducer;

export const selectProduct = (state: RootState) => {
  return state.customizer.product;
};

export const selectCreatedProduct = (state: RootState) => {
  return state.customizer.createdProduct;
};

export const selectSelectedShippingPriority = (state: RootState) => {
  return state.customizer.selectedShippingPriority;
};
export const selectStoreData = (state: RootState) => {
  return state.customizer.storeData;
};
export default customizerReducer;
